import { useState, useEffect } from "react";
import { CT } from "@/content";
import { RichText } from "@/components/RichText";
import { StudentBanners } from "@/components/StudentBanners";
import { BottomNav, ProgressRing } from "@/components/student/BottomNav";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { NotificationBell } from "../components/NotificationBell";
import { usePushNotifications } from "../hooks/usePushNotifications";
import axios from "axios";
import { MyPracticeCard } from "@/components/classDetail/PracticeTab";
import { format } from "date-fns";
import {
  BookOpen, Calendar, Bell, Plus, LogOut, ChevronRight, Clock, TrendingUp, Trophy,
  History, Flame, Bot, Award, Download, KeyRound, Zap, Lock, Coins, Target, Brain,
  Calculator, Eye, Divide, Lightbulb, Rocket, Gauge, Star
} from "lucide-react";
import { motion } from "framer-motion";

const ACTIVITY_CARDS = [
  { id: "brain-speed-trainer", title: "Brain Speed Trainer", tag: "Reaction & Speed", status: "active", route: "/brain-gym", icon: Zap, color: "#F59E0B", lip: "#D97706", bg: "#FFFBEB", border: "#FDE68A", text: "#B45309" },
  { id: "abacus-practice", title: "Abacus Practice", tag: "Visual Calculations", status: "active", route: "/abacus", icon: Calculator, color: "#10B981", lip: "#059669", bg: "#ECFDF5", border: "#A7F3D0", text: "#047857" },
  { id: "mental-maths", title: "Mental Maths", tag: "Speed Arithmetic", status: "active", route: "/mental-maths", icon: Brain, color: "#8B5CF6", lip: "#6D28D9", bg: "#F5F3FF", border: "#DDD6FE", text: "#6D28D9" },
  { id: "daily-challenge", title: "Daily Challenge", tag: "Earn Bonus XP", status: "active", route: "/arena", icon: Target, color: "#FF5722", lip: "#E64A19", bg: "#FBE9E7", border: "#FFCCBC", text: "#D84315" },
  { id: "maths-visualisation", title: "Maths Visualisation", tag: "Spatial Reasoning", status: "active", route: "/visualize", icon: Eye, color: "#06B6D4", lip: "#0891B2", bg: "#ECFEFF", border: "#A5F3FC", text: "#0E7490" },
  { id: "vedic-maths", title: "Vedic Maths", tag: "Shortcuts & Tricks", status: "active", route: "/vedic-maths", icon: Divide, color: "#EC4899", lip: "#BE185D", bg: "#FDF2F8", border: "#FBCFE8", text: "#BE185D" },
  { id: "mathbot", title: "MathBot AI Tutor", tag: "Instant Help 24/7", status: "active", route: "/ai-tutor", icon: Bot, color: "#3B82F6", lip: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF" },
  { id: "leaderboard", title: "Leaderboard", tag: "Compete & Rank", status: "active", route: "/leaderboard", icon: Trophy, color: "#EAB308", lip: "#CA8A04", bg: "#FEFCE8", border: "#FEF08A", text: "#A16207" },
];

const BADGE_ICONS = { Flame, Zap, Brain, Calculator, Target, Trophy, Lightbulb, Rocket };

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 12, scale: 0.96 }, show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 300, damping: 22 } } };

function StatChip({ icon: Icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2.5 bg-white rounded-2xl px-3 py-2.5 border-2 border-slate-100 min-w-[132px]">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${color.bg}`}>
        <Icon className={`w-5 h-5 ${color.fg}`} strokeWidth={2.4} />
      </div>
      <div className="leading-tight">
        <p className="text-base sm:text-lg font-extrabold text-slate-800 font-['Fredoka']">{value}</p>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      </div>
    </div>
  );
}

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  usePushNotifications(token);
  const [classes, setClasses] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [myCertificates, setMyCertificates] = useState([]);
  const [game, setGame] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState("");
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [joining, setJoining] = useState(false);

  const [pinDialog, setPinDialog] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);
  const [pinForm, setPinForm] = useState({ password: "", pin: "", confirmPin: "" });
  const [savingPin, setSavingPin] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [classesRes, announcementsRes, quizzesRes, assignmentsRes, gameRes] = await Promise.all([
        axios.get(`${API}/classes`, { headers }),
        axios.get(`${API}/announcements`, { headers }),
        axios.get(`${API}/quizzes`, { headers }),
        axios.get(`${API}/assignments`, { headers }),
        axios.get(`${API}/gamification/me`, { headers }),
      ]);
      setClasses(classesRes.data);
      setAnnouncements(announcementsRes.data.slice(0, 5));
      setQuizzes(quizzesRes.data.filter(q => !q.is_completed).slice(0, 5));
      setAssignments(assignmentsRes.data.slice(0, 5));
      setGame(gameRes.data);
      try { setEvents((await axios.get(`${API}/events/live`, { headers })).data.events || []); } catch (e) { /* ignore */ }
      try { setMyCertificates((await axios.get(`${API}/certificates`, { headers })).data); } catch (e) { /* ignore */ }
      try {
        const pinRes = await axios.get(`${API}/auth/has-pin`, { headers });
        setHasPin(pinRes.data.has_pin);
        setHasPassword(pinRes.data.has_password !== false);
      } catch (e) { /* ignore */ }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClass = async () => {
    if (!joinCode.trim()) { toast.error("Please enter a join code"); return; }
    setJoining(true);
    try {
      const response = await axios.post(`${API}/classes/request-join`, { join_code: joinCode }, { headers });
      toast.success(response.data.status === "pending" ? "Join request submitted! Waiting for teacher approval." : "Successfully joined class!");
      setJoinCode(""); setJoinDialogOpen(false); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join class");
    } finally { setJoining(false); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const handleDownloadCertificate = async (certificateId, filename) => {
    try {
      const response = await axios.get(`${API}/certificates/${certificateId}/download`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { toast.error("Failed to download certificate"); }
  };

  const handleSetPin = async () => {
    if (hasPassword && !pinForm.password) { toast.error("Please enter your current password"); return; }
    if (!pinForm.pin || pinForm.pin.length !== 4 || !/^\d{4}$/.test(pinForm.pin)) { toast.error("PIN must be exactly 4 digits"); return; }
    if (pinForm.pin !== pinForm.confirmPin) { toast.error("PINs do not match"); return; }
    setSavingPin(true);
    try {
      await axios.post(`${API}/auth/set-pin`, { password: pinForm.password, pin: pinForm.pin }, { headers });
      toast.success(hasPin ? "PIN updated!" : "PIN set successfully!");
      setHasPin(true); setPinDialog(false); setPinForm({ password: "", pin: "", confirmPin: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set PIN");
    } finally { setSavingPin(false); }
  };

  const getUpcomingDeadlines = () => {
    const now = new Date();
    return assignments.filter(a => a.due_date && new Date(a.due_date) > now).slice(0, 3);
  };

  const motivational = () => {
    if (!game) return "Let's train your Maths Brain today!";
    if (game.daily_goal.completed) return "Goal smashed! You've completed today's training 🎉";
    if (game.daily_goal.minutes_done > 0) {
      const left = Math.ceil(game.daily_goal.goal_minutes - game.daily_goal.minutes_done);
      return `Just ${left} more minute${left === 1 ? "" : "s"} to hit today's goal!`;
    }
    const xpLeft = game.xp_for_next - game.xp_into_level;
    return `You're ${xpLeft} XP away from Level ${game.level + 1}!`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || "Student";

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Lexend'] pb-24 md:pb-8" data-testid="student-dashboard">
      {/* Greeting header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b-2 border-slate-100" data-testid="student-greeting-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            <button onClick={() => navigate("/student")} className="flex items-center gap-2.5" data-testid="student-avatar-button">
              <div className="w-10 h-10 rounded-full border-2 border-indigo-500 bg-indigo-50 flex items-center justify-center text-indigo-600 font-extrabold font-['Fredoka'] text-lg">
                {firstName[0]?.toUpperCase()}
              </div>
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="text-base font-bold text-slate-800 font-['Fredoka']">TheMatheMind <span className="text-amber-500">360</span></span>
                <span className="text-[10px] text-slate-400 tracking-wider uppercase">Maths Brain Platform</span>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <div data-testid="header-streak-chip" className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border-2 border-amber-200 rounded-full text-amber-700 font-bold text-sm">
                <Flame className="w-4 h-4 text-orange-500" />
                {game?.current_streak || 0}
              </div>
              <div data-testid="header-coins-chip" className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-50 border-2 border-yellow-200 rounded-full text-yellow-700 font-bold text-sm">
                <Coins className="w-4 h-4 text-yellow-500" />
                {game?.coins || 0}
              </div>
              <NotificationBell token={token} />
              <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-slate-700 rounded-full">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6 space-y-6">
        {/* Hero greeting */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 font-['Fredoka'] tracking-wide">
            Hello, {firstName}! <span className="inline-block">👋</span>
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-0.5 font-medium">Ready to train your Maths Brain?</p>
        </div>

        <StudentBanners />

        {events.length > 0 && (
          <div className="mb-4" data-testid="student-events">
            <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5 font-['Fredoka']">
              <Calendar className="w-4 h-4 text-indigo-500" /> Upcoming Events
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {events.slice(0, 4).map((ev) => (
                <div key={ev.event_id} className="rounded-2xl border border-indigo-100 bg-white p-4" data-testid={`student-event-${ev.event_id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{ev.mode === "online" ? "ONLINE" : "IN PERSON"}</span>
                    {ev.date && <span className="text-[11px] text-slate-500">{ev.date}{ev.start_time ? ` · ${ev.start_time}` : ""}</span>}
                  </div>
                  <p className="font-bold text-slate-800 leading-tight">{ev.name}</p>
                  {ev.description && <RichText html={ev.description} className="rich-compact text-[12px] text-slate-500 mt-0.5 max-h-48 overflow-hidden" data-testid={`event-desc-${ev.event_id}`} />}
                  {(ev.meeting_link || ev.registration_link) && (
                    <a href={ev.registration_link || ev.meeting_link} target="_blank" rel="noreferrer"
                      className="inline-block mt-2 text-[12px] font-semibold text-indigo-600 hover:underline" data-testid={`student-event-link-${ev.event_id}`}>
                      {ev.registration_link ? "Register →" : "Join link →"}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stat chips */}
        <div className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 lg:grid-cols-6 scrollbar-hide">
          <StatChip testid="stat-streak-chip" icon={Flame} label="Streak" value={`${game?.current_streak || 0} day${(game?.current_streak || 0) === 1 ? "" : "s"}`} color={{ bg: "bg-orange-50", fg: "text-orange-500" }} />
          <StatChip testid="stat-xp-chip" icon={Zap} label="Today's XP" value={`${game?.xp_today || 0}`} color={{ bg: "bg-yellow-50", fg: "text-yellow-500" }} />
          <StatChip testid="stat-level-chip" icon={Award} label="Level" value={`Lvl ${game?.level || 1}`} color={{ bg: "bg-indigo-50", fg: "text-indigo-500" }} />
          <StatChip testid="stat-accuracy-chip" icon={Target} label="Accuracy" value={`${game?.accuracy || 0}%`} color={{ bg: "bg-emerald-50", fg: "text-emerald-500" }} />
          <StatChip testid="stat-speed-chip" icon={Gauge} label="Avg / Qn" value={game?.speed_seconds ? `${game.speed_seconds}s` : "—"} color={{ bg: "bg-cyan-50", fg: "text-cyan-500" }} />
          <StatChip testid="stat-brain-chip" icon={Brain} label="Brain Score" value={`${game?.brain_score || 0}`} color={{ bg: "bg-purple-50", fg: "text-purple-500" }} />
        </div>

        {/* Today's goal + XP/level */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="rounded-2xl border-2 border-slate-100 shadow-sm lg:col-span-1" data-testid="today-goal-card">
            <CardContent className="p-5 flex items-center gap-5">
              <ProgressRing percent={game?.daily_goal.percent || 0} size={118} stroke={12} data-testid="today-goal-progress-ring">
                <span className="text-2xl font-extrabold text-indigo-600 font-['Fredoka']">{game?.daily_goal.percent || 0}%</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">done</span>
              </ProgressRing>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Today's Goal</p>
                <p className="text-xl font-bold text-slate-800 font-['Fredoka']">
                  {game?.daily_goal.minutes_done || 0} / {game?.daily_goal.goal_minutes || 10} min
                </p>
                <p className="text-sm text-slate-500 mt-1 leading-snug">{motivational()}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-2 border-slate-100 shadow-sm lg:col-span-2" data-testid="xp-level-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Star className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800 font-['Fredoka']">Level {game?.level || 1}</p>
                    <p className="text-xs text-slate-400 font-semibold">{game?.xp_total || 0} XP total</p>
                  </div>
                </div>
                <span className="text-sm font-bold text-indigo-600">{game?.xp_into_level || 0} / {game?.xp_for_next || 100} XP</span>
              </div>
              <div className="h-4 rounded-full bg-indigo-100 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-700" style={{ width: `${game?.level_percent || 0}%` }} data-testid="xp-progress-bar" />
              </div>
              <p className="text-xs text-slate-400 mt-2 font-medium">Next level in {(game?.xp_for_next || 100) - (game?.xp_into_level || 0)} XP · keep going!</p>
            </CardContent>
          </Card>
        </div>

        {/* Activity cards */}
        <div>
          <MyPracticeCard token={token} />
          <h2 className="text-xl font-bold text-slate-800 font-['Fredoka'] mb-3">Train Your Brain</h2>
          <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {ACTIVITY_CARDS.map((c) => {
              const Icon = c.icon;
              const locked = c.status === "soon";
              return (
                <motion.button
                  key={c.id}
                  variants={item}
                  data-testid={`activity-card-${c.id}`}
                  onClick={() => { if (locked) { toast("Unlocking soon — stay tuned! 🚀"); } else { navigate(c.route); } }}
                  className="relative overflow-hidden rounded-2xl border-2 border-b-4 p-4 flex flex-col items-start justify-between min-h-[138px] text-left transition-all duration-150 hover:-translate-y-1 active:translate-y-0.5 active:border-b-2"
                  style={{ backgroundColor: c.bg, borderColor: c.border, borderBottomColor: c.lip }}
                >
                  {locked && (
                    <div className="absolute top-2 right-[-30px] rotate-45 bg-slate-800/85 text-white text-[9px] font-bold px-9 py-0.5 tracking-wider uppercase z-10 flex items-center gap-1">
                      <Lock className="w-2.5 h-2.5" /> Soon
                    </div>
                  )}
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm" style={{ backgroundColor: c.color }}>
                    <Icon className="w-6 h-6 text-white" strokeWidth={2.4} />
                  </div>
                  <div className="mt-2">
                    <p className="text-sm sm:text-base font-bold font-['Fredoka'] leading-tight" style={{ color: c.text }}>{c.title}</p>
                    <p className="text-[11px] font-semibold mt-0.5" style={{ color: c.text, opacity: 0.7 }}>{c.tag}</p>
                  </div>
                </motion.button>
              );
            })}
          </motion.div>
        </div>

        {/* Badges */}
        {game?.achievements && (
          <div>
            <h2 className="text-xl font-bold text-slate-800 font-['Fredoka'] mb-3">Your Badges</h2>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2.5 sm:gap-3">
              {game.achievements.map((b) => {
                const Icon = BADGE_ICONS[b.icon] || Award;
                return (
                  <div
                    key={b.id}
                    data-testid={`badge-item-${b.id.replace(/_/g, '-')}`}
                    title={b.earned ? b.name : (b.locked_reason || "Keep going to unlock!")}
                    className={`rounded-xl p-2.5 flex flex-col items-center text-center border-2 ${b.earned ? "bg-gradient-to-b from-amber-50 to-orange-50 border-amber-300 shadow-sm" : "bg-slate-50 border-dashed border-slate-200 opacity-60 grayscale"}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${b.earned ? "bg-amber-400" : "bg-slate-300"}`}>
                      {b.earned ? <Icon className="w-5 h-5 text-white" /> : <Lock className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-[9px] sm:text-[10px] font-bold text-slate-600 leading-tight">{b.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick links */}
        <div className="flex flex-wrap gap-2">
          <Button data-testid="secure-exams-btn" onClick={() => navigate("/secure-exams")} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm px-4 font-bold">
            <Trophy className="w-4 h-4 mr-1.5" /> <CT k="olympiad_name" />
          </Button>
          <Button data-testid="quiz-history-btn" variant="outline" onClick={() => navigate("/quiz-history")} className="rounded-full text-xs sm:text-sm px-4 border-2">
            <History className="w-4 h-4 mr-1.5" /> History
          </Button>
          <Button data-testid="analytics-btn" variant="outline" onClick={() => navigate("/analytics")} className="rounded-full text-xs sm:text-sm px-4 border-2">
            <TrendingUp className="w-4 h-4 mr-1.5" /> Progress
          </Button>
          <Button data-testid="set-pin-btn" variant="outline" onClick={() => setPinDialog(true)} className="rounded-full text-xs sm:text-sm px-4 border-2">
            <KeyRound className="w-4 h-4 mr-1.5" /> {hasPin ? "Change PIN" : "Set PIN"}
          </Button>
        </div>

        {/* My Learning */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <Card className="rounded-2xl border-2 border-slate-100 shadow-sm">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
                <div>
                  <CardTitle className="font-['Fredoka'] text-lg">My Classes</CardTitle>
                  <CardDescription>Classes you're enrolled in</CardDescription>
                </div>
                <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="join-class-btn" className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white">
                      <Plus className="w-4 h-4 mr-2" /> Join Class
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-['Fredoka']">Join a Class</DialogTitle>
                      <DialogDescription>Enter the class code provided by your teacher</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label htmlFor="join-code">Class Code</Label>
                        <Input data-testid="join-code-input" id="join-code" placeholder="e.g., ABC12345" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} className="uppercase" />
                      </div>
                      <Button data-testid="submit-join-btn" onClick={handleJoinClass} className="w-full rounded-full bg-indigo-600 text-white" disabled={joining}>
                        {joining ? "Joining..." : "Join Class"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {classes.length === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">No classes yet</p>
                    <p className="text-sm text-slate-400">Join a class to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {classes.map((cls) => (
                      <motion.div key={cls.class_id} whileHover={{ scale: 1.01 }}
                        className="p-4 rounded-xl border-2 border-slate-100 hover:border-indigo-200 cursor-pointer transition-all"
                        onClick={() => navigate(`/class/${cls.class_id}`)} data-testid={`class-card-${cls.class_id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-bold text-slate-800">{cls.name}</h3>
                            <p className="text-sm text-slate-500">{cls.subject} • {cls.teacher_name}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-300" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-2 border-slate-100 shadow-sm">
              <CardHeader>
                <CardTitle className="font-['Fredoka'] text-lg">Available Quizzes</CardTitle>
                <CardDescription>Quizzes waiting for you</CardDescription>
              </CardHeader>
              <CardContent>
                {quizzes.length === 0 ? (
                  <p className="text-center text-slate-400 py-4">No quizzes available</p>
                ) : (
                  <div className="space-y-3">
                    {quizzes.map((quiz) => (
                      <div key={quiz.quiz_id} className="p-4 rounded-xl border-2 border-slate-100 hover:border-indigo-200 cursor-pointer transition-all"
                        onClick={() => navigate(`/quiz/${quiz.quiz_id}`)} data-testid={`quiz-card-${quiz.quiz_id}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <h3 className="font-bold text-slate-800">{quiz.title}</h3>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                              <span className="text-sm text-slate-500">{quiz.question_count} questions</span>
                              {quiz.is_timed && (<span className="text-sm text-orange-600 flex items-center gap-1"><Clock className="w-3 h-3" />{quiz.time_limit_minutes} min</span>)}
                            </div>
                          </div>
                          <Button size="sm" className="rounded-full bg-indigo-600 text-white shrink-0">Start</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-5">
            <Card className="rounded-2xl border-2 border-slate-100 shadow-sm">
              <CardHeader><CardTitle className="text-base font-['Fredoka'] flex items-center gap-2"><Calendar className="w-5 h-5 text-indigo-500" />Upcoming Deadlines</CardTitle></CardHeader>
              <CardContent>
                {getUpcomingDeadlines().length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-2">No upcoming deadlines</p>
                ) : (
                  <div className="space-y-3">
                    {getUpcomingDeadlines().map((a) => (
                      <div key={a.assignment_id} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                        <div>
                          <p className="text-sm font-semibold text-slate-700">{a.title}</p>
                          <p className="text-xs text-slate-400">{a.due_date ? `Due ${format(new Date(a.due_date), "MMM d, h:mm a")}` : "No due date"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-2 border-slate-100 shadow-sm">
              <CardHeader><CardTitle className="text-base font-['Fredoka'] flex items-center gap-2"><Bell className="w-5 h-5 text-indigo-500" />Announcements</CardTitle></CardHeader>
              <CardContent>
                {announcements.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-2">No announcements</p>
                ) : (
                  <div className="space-y-3">
                    {announcements.map((ann) => (
                      <div key={ann.announcement_id} className="border-b border-slate-100 pb-3 last:border-0">
                        <p className="text-sm font-semibold text-slate-700">{ann.title}</p>
                        <p className="text-xs text-slate-400 line-clamp-2 whitespace-pre-wrap">{ann.content}</p>
                        <p className="text-xs text-slate-300 mt-1">{format(new Date(ann.created_at), "MMM d")}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-2 border-slate-100 shadow-sm" data-testid="my-certificates-section">
              <CardHeader><CardTitle className="text-base font-['Fredoka'] flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" />My Certificates</CardTitle></CardHeader>
              <CardContent>
                {myCertificates.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-2">No certificates yet</p>
                ) : (
                  <div className="space-y-3">
                    {myCertificates.map((cert) => (
                      <div key={cert.certificate_id} className="flex items-center justify-between p-3 rounded-lg border-2 border-slate-100 hover:border-amber-200 transition-all" data-testid={`cert-${cert.certificate_id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center"><Award className="w-4 h-4 text-amber-600" /></div>
                          <div>
                            <p className="text-sm font-semibold text-slate-700">{cert.title}</p>
                            <p className="text-xs text-slate-400">{cert.teacher_name} · {format(new Date(cert.created_at), "MMM d, yyyy")}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => handleDownloadCertificate(cert.certificate_id, cert.original_filename)} data-testid={`download-cert-${cert.certificate_id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <BottomNav />

      {/* Set PIN Dialog */}
      <Dialog open={pinDialog} onOpenChange={(open) => { setPinDialog(open); if (!open) setPinForm({ password: "", pin: "", confirmPin: "" }); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Fredoka']">{hasPin ? "Change Your PIN" : "Set Your PIN"}</DialogTitle>
            <DialogDescription>{hasPin ? "Update your 4-digit PIN for quick login" : "Create a 4-digit PIN to login without email"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {hasPassword && (
              <div className="space-y-2">
                <Label>Current Password</Label>
                <Input data-testid="pin-password-input" type="password" placeholder="Enter your password" value={pinForm.password} onChange={(e) => setPinForm({ ...pinForm, password: e.target.value })} />
              </div>
            )}
            <div className="space-y-2">
              <Label>4-Digit PIN</Label>
              <Input data-testid="pin-new-input" type="text" inputMode="numeric" maxLength={4} placeholder="e.g. 1234" value={pinForm.pin} onChange={(e) => setPinForm({ ...pinForm, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
            </div>
            <div className="space-y-2">
              <Label>Confirm PIN</Label>
              <Input data-testid="pin-confirm-input" type="text" inputMode="numeric" maxLength={4} placeholder="Re-enter PIN" value={pinForm.confirmPin} onChange={(e) => setPinForm({ ...pinForm, confirmPin: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
            </div>
            <Button data-testid="pin-save-btn" className="w-full rounded-full bg-indigo-600 text-white" onClick={handleSetPin} disabled={savingPin}>
              {savingPin ? "Saving..." : hasPin ? "Update PIN" : "Set PIN"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
